import { useEffect, useState, useMemo, useRef } from "react";
import { createRoot } from "react-dom/client";
import poloImage from "@assets/imgs/poloImage.svg";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getProviderInformation, providerInformationsSelector } from "../../../../../features/senninha/GestaoEquipe/providerInformationsSlice";
import { hubsSelector } from "../../../../../features/senninha/hubsSlice";
import DrawerCollectionHub from "./DrawerCollectionHub";
import { pinsSelector } from "../../../../../features/senninha/pinsSlice";

import {
    manageSelectedRelocatePins,
    routesManagementSelector,
    setUnassociatedPinAnyData
} from "../../../../../features/senninha/routesManagementSlice";

import * as S from "./styles";

const CollectionHubMarkerUI = ({ getProviderInformationData, totalColetas, isSelected }) => {
    return (
        <S.CollectionHubContainer isSelected={isSelected}>
            <S.CollectionHubMarker isSelected={isSelected}>
                {totalColetas} coletas no polo - {getProviderInformationData?.nome_fantasia || "Polo"}
            </S.CollectionHubMarker>
            <img src={poloImage} alt="Icone Polo" />
        </S.CollectionHubContainer>
    );
};

const CollectionHub = ({ map }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [open, setOpen] = useState(false);

    const { selectedHubInfos } = useSelector(hubsSelector);
    const { filteredPins, mapWorkflow, pins } = useSelector(pinsSelector);
    const { getProviderInformationData } = useSelector(providerInformationsSelector);

    const { selectedRelocatePins } = useSelector(routesManagementSelector);

    const markerRef = useRef(null);
    const rootRef = useRef(null);
    const containerRef = useRef(document.createElement("div"));

    const validPins = useMemo(() => {
        if (!filteredPins || filteredPins.length === 0) return [];

        const grouped = filteredPins.reduce((acc, pin) => {
            if (pin.tipo?.toLowerCase() !== "any") return acc;

            const filteredOss = pin.oss?.filter(os => os.status_pathway === "COLETA NO POLO") || [];
            const isPinColetaNoPolo = pin.status_pathway === "COLETA NO POLO";

            if (filteredOss.length === 0 && !isPinColetaNoPolo) return acc;

            const date = pin.data_limite || (pin.oss && pin.oss[0]?.deadline_date);
            const doc = pin.documento_cliente;
            const groupKey = `${doc}_${date}`;

            if (!acc[groupKey]) {
                acc[groupKey] = {
                    ...pin,
                    servico: pin.servico || 'COLETA',
                    ids_agrupados: [pin.id_atendimento],
                    oss: filteredOss.length > 0 ? filteredOss : (pin.oss || [])
                };
            } else {
                if (!acc[groupKey].ids_agrupados.includes(pin.id_atendimento)) {
                    acc[groupKey].ids_agrupados.push(pin.id_atendimento);
                    acc[groupKey].oss.push(...(pin.oss || []));
                }
            }

            return acc;
        }, {});

        return Object.values(grouped);
    }, [filteredPins]);

    const hasSelectedPins = useMemo(() => {
        const hasDirectSelection = validPins.some(vp =>
            selectedRelocatePins.some(srp => srp.id === vp.id)
        );

        const isPoloRootSelected = selectedRelocatePins.some(srp => srp.id === 'polo-root');

        return hasDirectSelection || isPoloRootSelected;
    }, [validPins, selectedRelocatePins]);

    const totalColetas = useMemo(() => {
        return validPins.reduce((acc, curr) => acc + (curr.oss?.length || 0), 0) || 0;
    }, [validPins]);

    const handleHubClick = () => {
        if (mapWorkflow === 'routesDrawer-relocation') {
            const allSubPins = validPins.flatMap((dataPin) =>
                (dataPin.oss || []).map((os) => ({
                    ...dataPin,
                    originalId: dataPin.id,
                    id: `polo-${dataPin.id}-${os.id_os || os.id_atendimento}`,
                    id_atendimento: os.id_atendimento,
                    servico: 'ENTREGA',
                    tipo: 'any',
                    oss: [os],
                    status_pathway: "COLETA NO POLO"
                }))
            );

            const allOssAgrupadas = allSubPins.flatMap(p => p.oss);

            const mockUnassociatedData = {
                unassociatedPin: {
                    id: 'polo-root',
                    servico: 'COLETA',
                    tipo: 'any',
                    status_pathway: "COLETA NO POLO",
                    associatedPinsIDs: allSubPins.map((p) => p.id),
                    oss: allOssAgrupadas
                },
                deliveriesPins2BeAssociated: allSubPins
            };

            dispatch(setUnassociatedPinAnyData(mockUnassociatedData));
            dispatch(manageSelectedRelocatePins(mockUnassociatedData.unassociatedPin));

        } else {
            setOpen(true);
        }
    };
    useEffect(() => {
        if (selectedHubInfos?.children) {
            dispatch(getProviderInformation({ polo: selectedHubInfos.children }));
        }
    }, [dispatch, selectedHubInfos]);

    useEffect(() => {
        if (!map || !window.google?.maps?.marker || !getProviderInformationData) return;

        if (!rootRef.current) {
            rootRef.current = createRoot(containerRef.current);
        }

        rootRef.current.render(
            <CollectionHubMarkerUI
                getProviderInformationData={getProviderInformationData}
                totalColetas={totalColetas}
                isSelected={hasSelectedPins}
            />
        );

        if (!markerRef.current) {
            markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
                map,
                content: containerRef.current,
            });
        }

        google.maps.event.clearListeners(markerRef.current, 'click');
        const listener = markerRef.current.addListener("click", handleHubClick);

        markerRef.current.position = {
            lat: getProviderInformationData?.latitude ? parseFloat(getProviderInformationData?.latitude.replace(",", ".")) : 0,
            lng: getProviderInformationData?.longitude ? parseFloat(getProviderInformationData?.longitude.replace(",", ".")) : 0
        };

        return () => {
            listener.remove();
        };

    }, [map, getProviderInformationData, validPins, totalColetas, mapWorkflow, selectedRelocatePins, hasSelectedPins]);

    useEffect(() => {
        return () => {
            if (markerRef.current) markerRef.current.map = null;
            if (rootRef.current) {
                const rootToUnmount = rootRef.current;
                setTimeout(() => rootToUnmount.unmount(), 0);
            }
        };
    }, []);

    const editHub = () => {
        history.push({
            pathname: "/lmp/senninha/gestao-de-equipe",
            state: { openProviderEditDrawer: true }
        });
    };

    return (
        <DrawerCollectionHub
            open={open}
            setOpen={setOpen}
            editHub={editHub}
            getProviderInformationData={getProviderInformationData}
            pins={validPins}
        />
    );
};

export default CollectionHub;